import { log, warn } from '@execonline-inc/logging';
import { assertNever, pipe } from '@kofno/piper';
import {
  Buffering,
  Ended,
  Initialized,
  Kettle,
  Paused,
  Playing,
  Ready,
  VideoState,
} from '@execonline-inc/kettle.private';
import { Maybe, fromNullable, just, nothing } from 'maybeasy';
import { IReactionDisposer, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Task } from 'taskarian';
import { warnAndNotify } from '../../../Honeybadger';
import {
  JWPlayerFn,
  JWPlayerInitializationError,
  JWPlayerObj,
  MetaEvent,
  TracksSourceFile,
  VideoSourceFile,
  thumbnailsSourceFile,
} from '../../JWPlayer/Types';
import { loadJWPlayer } from './Loader';
import { ReqHlsPlayerOptions, prepareOptions } from './Types';
import * as style from './style.module.css';

export interface Props {
  id: string;
  className: string;
  kettle: Kettle;
  options: Task<JWPlayerInitializationError, ReqHlsPlayerOptions>;
  fallbackSources?: VideoSourceFile[];
}

class JWPlayer extends React.Component<Props, {}> {
  private container?: HTMLDivElement | null;
  private stopReactions?: IReactionDisposer;

  registerKettleReactions = (playerObj: JWPlayerObj) => {
    const { kettle } = this.props;
    return reaction(
      () => kettle.videoMessage.length,
      (_length: number) => {
        kettle.popMessage().map((msg) =>
          msg.cata({
            play: () => {
              playerObj.play();
            },
            pause: () => {
              playerObj.pause();
            },
            seekTo: (pos) => {
              playerObj.seek(pos);
            },
          }),
        );
      },
    );
  };

  updateKettle = (
    playerObj: JWPlayerObj,
    fn: (pos: Maybe<number>, duration: Maybe<number>) => VideoState,
  ) => {
    const { kettle } = this.props;
    const pos = just(playerObj.getPosition());
    const dur = just(playerObj.getDuration());
    kettle.setVideoState(fn(pos, dur));
  };

  ready = (playerObj: JWPlayerObj) =>
    this.updateKettle(playerObj, (pos, dur) => new Ready(pos, dur));

  handlePlay = (playerObj: JWPlayerObj) => () =>
    this.updateKettle(playerObj, (pos, dur) => new Playing(pos, dur));

  handlePause = (playerObj: JWPlayerObj) => () =>
    this.updateKettle(playerObj, (pos, dur) => new Paused(pos, dur));

  handleEnd = (playerObj: JWPlayerObj) => () =>
    this.updateKettle(playerObj, (pos, dur) => new Ended(pos, dur));

  handleTime = (playerObj: JWPlayerObj) => () => {
    const { kettle } = this.props;
    this.updateKettle(playerObj, (pos, dur) =>
      kettle.videoState.cata({
        playing: () => new Playing(pos, dur),
        paused: () => new Paused(pos, dur),
        ended: () => new Ended(pos, dur),
        buffering: () => new Buffering(pos, dur),
        ready: () => new Ready(pos, dur),
        initialized: () => new Initialized(),
      }),
    );
  };

  handleFallback = (err: string, playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => {
    warn(`Initial setup failed for ${this.props.id}`, err);
    if (this.props.fallbackSources) {
      warn(`Attempting fallback for ${this.props.id}`, this.props.fallbackSources);
      const refinedOptions: ReqHlsPlayerOptions = {
        ...options,
        file: nothing(),
        sources: fromNullable(this.props.fallbackSources),
      };
      this.setup(playerObj, refinedOptions);
    } else {
      warnAndNotify(
        'Lecture video failed to load',
        `No fallback sources for ${this.props.id}`,
        this.props,
      );
    }
  };

  setAspectRatio = (playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => (e: MetaEvent) => {
    const newOptions = { ...options, aspectratio: just(`${e.width}:${e.height}`) };
    this.finalSetup(playerObj, newOptions);
  };

  setup = (playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => {
    if (options.dynamicallySetAspectRatio) {
      playerObj.setup(prepareOptions(options));
      playerObj.on('ready', () => {
        this.stopReactions = this.registerKettleReactions(playerObj);
        playerObj.on('meta', this.setAspectRatio(playerObj, options));
      });
    } else {
      this.finalSetup(playerObj, options);
    }
  };

  finalSetup = (playerObj: JWPlayerObj, options: ReqHlsPlayerOptions) => {
    playerObj.setup(prepareOptions(options));
    playerObj.on('ready', () => {
      this.stopReactions = this.registerKettleReactions(playerObj);
      this.ready(playerObj);
      playerObj.on('play', this.handlePlay(playerObj));
      playerObj.on('pause', this.handlePause(playerObj));
      playerObj.on('time', this.handleTime(playerObj));
      playerObj.on('seeked', this.handleTime(playerObj));
      playerObj.on('ended', this.handleEnd(playerObj));
      playerObj.on('meta', this.handleTime(playerObj));
      playerObj.on('complete', this.handleEnd(playerObj));
    });
  };

  initializationError = (err: JWPlayerInitializationError): void => {
    switch (err.kind) {
      case 'thumbnail-preview-missing-error':
        warnAndNotify('ThumbnailPreviewMissingError', ` ${err.key}`, err);
        break;
      case 'sprite-missing-error':
        warnAndNotify('SpriteMissingError', ` ${err.key}`, err);
        break;
      case 'subtitle-link-missing-error':
        warnAndNotify('SubtitleLinkMissingError', `Subtitle Link is missing: ${err.key}`, err);
        break;
      case 'low-stream-missing-error':
        warnAndNotify('LowStreamMissingError', `Low stream link is missing: ${err.key}`, err);
        break;
      case 'medium-stream-missing-error':
        warnAndNotify('MediumStreamMissingError', `Medium stream link is missing: ${err.key}`, err);
        break;
      case 'high-stream-missing-error':
        warnAndNotify('HighStreamMissingError', `High stream link is missing: ${err.key}`, err);
        break;
      case 'missing-link-error':
        warnAndNotify('MissingLinkError', `Link is missing: ${err.kind} ${err.rel}`, err);
        break;
      case 'missing-var-error':
        warnAndNotify(
          'MissingVarError',
          `Unable to load JWPlayer: Missing environment variable ${err.key}`,
          err,
        );
        break;
      case 'jwplayer-load-error':
        warnAndNotify('JWPlayerLoadError', err.key, err);
        break;
      default:
        assertNever(err);
    }
  };

  initializationSuccess = (options: ReqHlsPlayerOptions, player: JWPlayerFn) => {
    if (!this.container) {
      return;
    }
    const playerObj = player(this.container);
    log(`JWPlayer options for ${this.props.id}`, options);
    this.setup(playerObj, options);

    options.autoLoadIndex.do((index) => playerObj.setCurrentCaptions(index));

    playerObj.on('error', () => {
      this.handleFallback('error', playerObj, options);
    });
    playerObj.on('setupError', () => {
      this.handleFallback('setupError', playerObj, options);
    });
  };

  addSpriteAndCaptions = (options: ReqHlsPlayerOptions): ReqHlsPlayerOptions => {
    const { sprite, subtitles } = options;
    const tracks = just({})
      .assign('sprite', sprite)
      .assign('subtitles', subtitles)
      .map<TracksSourceFile[]>(({ sprite, subtitles }) => [
        thumbnailsSourceFile(sprite),
        ...subtitles,
      ])
      .getOrElseValue([]);
    return {
      ...options,
      tracks: tracks,
      sprite: nothing(),
      subtitles: nothing(),
    };
  };

  addPreviewImage = (options: ReqHlsPlayerOptions): ReqHlsPlayerOptions => {
    return options.thumbnailImage
      .map<ReqHlsPlayerOptions>((thumb) => ({
        ...options,
        image: thumb,
        thumbnailImage: nothing(),
      }))
      .getOrElseValue(options);
  };

  checkForSources = (options: ReqHlsPlayerOptions): ReqHlsPlayerOptions => {
    return options.sources
      .map<ReqHlsPlayerOptions>((sources) => {
        return sources.length === 0 ? { ...options, sources: nothing() } : options;
      })
      .getOrElseValue(options);
  };

  finalizeOptions: (options: ReqHlsPlayerOptions) => ReqHlsPlayerOptions = pipe(
    this.checkForSources,
    this.addSpriteAndCaptions,
    this.addPreviewImage,
  );

  componentDidMount() {
    Task.succeed<JWPlayerInitializationError, {}>({})
      .assign('options', this.props.options)
      .assign('player', ({ options }) => loadJWPlayer(options.playerUrl, options.playerKey))
      .fork(this.initializationError, ({ options, player }) => {
        this.initializationSuccess(this.finalizeOptions(options), player);
      });
  }

  componentWillUnmount() {
    if (window.jwplayer && this.container) {
      window.jwplayer(this.container).remove();
    }

    if (this.stopReactions) {
      this.stopReactions();
    }
  }

  refContainer = (container: HTMLDivElement | null) => {
    this.container = container;
  };

  render() {
    const { id, className } = this.props;
    return (
      <div
        id={id}
        className={`${className} ${style.root}`}
        ref={this.refContainer}
        data-test-jwplayer={id}
      />
    );
  }
}

export default observer(JWPlayer);
